import { FC, FunctionComponent, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { createSectionByIdSelector } from '../../../redux/pages/selectors';
import { useDispatch, useSelector } from 'react-redux';
import useTitleBarSelect from '../../../hooks/useTitleBarSelect';
import {
  addAdditionalTitleBarComponents,
  removeTitleBarComponent,
  toggleTitleBarLoading,
} from '../../../redux/pages/actions';
import { Select } from '../../../types/redux/pages/PagesStore';
import {
  APPEND_NEW_SELECT_TO_TITLEBAR,
  REMOVE_SELECT_FROM_TITLEBAR,
} from '../../../types/redux/pages/pageTypes';
import { clientNameSelector } from '../../../redux/auth/selectors';
import GridItem from '../../layout/GridComponents/GridItem';
import AggregateExposureTable from './exposureComponents/AggregateExposureTable';
import AssetGrossExposure from './exposureComponents/AssetGrossExposure';
import AssetClassGrossExposureChart from './exposureComponents/AssetClassGrossExposure';
import AssetCurrencyExposure from './exposureComponents/AssetCurrencyExposure';
import CountryCodeGrossExposure from './exposureComponents/CountryCodeGrossExposure';
import AssetCountryCodeExposure from './exposureComponents/AssetCountryCodeExposure';
import AmlLevelGrossExposure from './exposureComponents/AmlLevelGrossExposure';
import AssetAmlLevelExposure from './exposureComponents/AssetAmlLevelExposure';
import { FormControlLabel, Switch } from '@mui/material';
import { PresetExposureType } from '../../tables/ultraTable/types/column.types';
import UltraTable, {
  TableData,
  UltraTablePresetConfig,
} from '../../tables/ultraTable/UltraTable';

import { ultraTableStyles } from '../../tables/ultraTable/styles/ultratable.styles';
import { Position } from '../../tables/ultraTable/types/position.types';
import UnderlyingPositionTable from './UnderlyingPositionTable';

import { ClassNameMap } from '@mui/styles';
import {
  buildColumns,
  buildData,
} from '../../tables/ultraTable/utils/buildUltraTableData';
import {
  ALL_EXPOSURE_PRESETS,
  GeneralPreset,
} from '../../tables/ultraTable/data/ultrapresetColumnData';
import { ALL_EXPOSURE_COLUMNS } from '../../tables/ultraTable/data/presetColumnData';
import { UltraTablePreset } from '../../tables/ultraTable/types/presets.types';
import RaptorLoading from '../../feedback/RaptorLoading';
import { EditableFields } from '../../tables/ultraTable/types/editable.types';
import { formatDateForCheckingState } from '../../../utilities/dateFormatters';
import { AxiosInstance } from '../../../utilities/requestClient';
import { useQuery } from '@tanstack/react-query';
import NoDataMessage from '../../feedback/NoDataMessage.component';
import { UseQueryOptions } from '@tanstack/react-query';
import { raptorPlatformIdSelector } from '../../../redux/raptorPlatform/selectors';
import { FundInfoComponentProps } from '../../layout/general/SimpleGeneralPageWrapper';

const standardViewOptions = [
  {
    label: 'General Exposure',
    value: 'generalExposure',
  },
  {
    label: 'Country Exposure',
    value: 'countryExposure',
  },
];
const adepaViewOptions = [
  {
    label: 'General Exposure',
    value: 'generalExposure',
  },
  {
    label: 'Country Exposure',
    value: 'countryExposure',
  },
  {
    label: 'AML Exposure',
    value: 'amlExposure',
  },
];

function getViewOptions(fundId: string, clientName: string) {
  if (clientName === 'mersenne') {
    return adepaViewOptions;
    // I know this is not ideal but the aml exposure option is required for a demo so needs to be done for now.
    // IN future this can be moved to the config file to configure differnt options for different clients. DC - 2023-11-08
  } else if (fundId.startsWith('adepa') || fundId.startsWith('gemini')) {
    return adepaViewOptions;
  } else {
    return standardViewOptions;
  }
}

export function useExposureData(
  urlPrefix: 'aif_gem_data' | 'fund_gem_data',
  fundId: string,
  positionDate: string | undefined,
  fundTypes: string | undefined,
  raptorPlatformId?: string,
  options?: Omit<
    UseQueryOptions<
      FundExposureData,
      unknown,
      FundExposureData,
      [string, string | undefined, string | undefined]
    >,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) {
  return useQuery({
    queryKey: [
      fundId,
      urlPrefix,
      'exposure_data',
      positionDate,
      raptorPlatformId,
    ],
    queryFn: () =>
      getExposureData(urlPrefix, fundId, positionDate!, raptorPlatformId),
    enabled: !!positionDate && !!fundTypes,
  });
}

export const getExposureData = async (
  urlPrefix: 'aif_gem_data' | 'fund_gem_data',
  fundId: string,
  positionDate: string | undefined,
  raptorPlatformId?: string,
) => {
  const url = raptorPlatformId
    ? `${urlPrefix}/${fundId}/${positionDate}?raptor_platform_id=${raptorPlatformId}`
    : `${urlPrefix}/${fundId}/${positionDate}`;
  return (await AxiosInstance.get<FundExposureData>(url)).data;
};

// this is problematic, we need to combine the routes to not have aif and standard gem rotues separated,
// there have been so many issues resrtucturing that for now this hack will have to do to progress the project, Tom Walsh [2023-05-17]
const ExposureFund: FC<FundInfoComponentProps> = ({
  positionDate,
  fundId,
  section,
  fundName,
}) => {
  // Add Select for choosing the view
  const [viewOptions, setViewOptions] = useState<any>(null);

  const fundTypes = useSelector(createSectionByIdSelector(section))?.fundTypes;
  const raptorPlatformId = useSelector(raptorPlatformIdSelector);

  // this is problematic, we need to combine the routes to not have aif and standard gem rotues separated,
  // there have been so many issues resrtucturing that for now this hack will have to do to progress the project, Tom Walsh [2023-05-17]
  const urlPrefix = ['AIF', 'AIFMD'].some((r) => fundTypes?.includes(r))
    ? 'aif_gem_data'
    : 'fund_gem_data';

  const {
    data: exposureData,
    isLoading,
    error,
    isFetching,
  } = useExposureData(
    urlPrefix,
    fundId,
    positionDate,
    fundTypes,
    raptorPlatformId,
    {
      staleTime: 30 * 60 * 1000,
    },
  );

  const classes = ultraTableStyles();

  const clientName = useSelector(clientNameSelector) || 'mersenne';

  const dispatch = useDispatch();
  useEffect(() => {
    // check if the view options need to change.
    const updatedViewOptions = getViewOptions(fundId, clientName);
    // If the options haven't changes then dont do anything
    if (updatedViewOptions === viewOptions) return;
    dispatch(toggleTitleBarLoading(true));
    if (exposureData && !isLoading) {
      // Reset view options first
      setViewOptions(null);
      setViewOptions(updatedViewOptions);
    }
    return;
  }, [exposureData]);

  useEffect(() => {
    if (viewOptions) {
      const selectsToAdd: Select[] = [
        {
          titleBarTitle: 'Select View',
          titleBarKey: 'viewSelect',
          values: viewOptions,
          displayOnOverviewPage: false,
          aValueIsAlwaysSelected: true,
          currentValue: viewOptions[0].value,
        },
      ];
      dispatch(
        addAdditionalTitleBarComponents(
          selectsToAdd,
          APPEND_NEW_SELECT_TO_TITLEBAR,
        ),
      );
      dispatch(toggleTitleBarLoading(false));
    }

    return () => {
      dispatch(
        removeTitleBarComponent('viewSelect', REMOVE_SELECT_FROM_TITLEBAR),
      );
    };
  }, [viewOptions, positionDate, dispatch]);

  // Type safe the string
  const viewSelected = useTitleBarSelect('viewSelect') as PresetExposureType;

  if (isLoading) {
    return (
      <RaptorLoading
        centerWrap
        messages={[
          'Loading Manco Exposure Data...',
          'This may take a few seconds...',
        ]}
      />
    );
  }

  if (error) {
    return <NoDataMessage message={'Error Retrieving Data'} />;
  }

  if (!exposureData) {
    return <NoDataMessage message={`No data for ${positionDate}`} />;
  }

  return (
    <>
      <GridItem card xs={4} cardStyle={{ height: '100%' }} style={{ gap: 16 }}>
        <AggregateExposureTable
          fundId={fundId}
          fundName={fundName}
          data={exposureData}
          isFetching={isFetching}
        />
      </GridItem>
      <GridItem
        xs={4}
        style={{
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        }}
      >
        <AssetGrossExposure
          fundId={fundId}
          fundName={fundName}
          data={exposureData}
          isFetching={isFetching}
        />

        {/* ------------------------------ */}
        {/* display based on view selected */}
        {viewSelected === 'generalExposure' ? (
          <AssetClassGrossExposureChart
            data={exposureData}
            isFetching={isFetching}
          />
        ) : null}
        {viewSelected === 'countryExposure' ? (
          <CountryCodeGrossExposure
            data={exposureData}
            isFetching={isFetching}
          />
        ) : null}
        {viewSelected === 'amlExposure' ? (
          <AmlLevelGrossExposure data={exposureData} isFetching={isFetching} />
        ) : null}
        {/* ------------------------------ */}
      </GridItem>
      <GridItem xs={4} card cardStyle={{ height: '100%' }}>
        {/* ------------------------------ */}
        {/* display based on view selected */}
        {viewSelected === 'generalExposure' ? (
          <AssetCurrencyExposure
            fundId={fundId}
            fundName={fundName}
            data={exposureData}
            isFetching={isFetching}
          />
        ) : null}
        {viewSelected === 'countryExposure' ? (
          <AssetCountryCodeExposure
            fundId={fundId}
            fundName={fundName}
            data={exposureData}
            isFetching={isFetching}
          />
        ) : null}
        {viewSelected === 'amlExposure' ? (
          <AssetAmlLevelExposure
            fundId={fundId}
            fundName={fundName}
            data={exposureData}
            isFetching={isFetching}
          />
        ) : null}
        {/* ------------------------------ */}
        {/* TODO: The view selected feature will NOT WORK HERE! RF TO NEW PRESET FUNCTIONALITY */}
        {/* In order to make this reusable  */}
      </GridItem>

      <HedgePositionsTableWrapper
        originalData={exposureData}
        fundId={fundId}
        fundName={fundName}
        classes={classes}
        viewSelected={viewSelected}
        isFetching={isFetching}
      />
    </>
  );
};

export default ExposureFund;

export interface UltraTableWrapperProps {
  originalData: any; // Keeping any because lazy to make union type of manco/exposure
  fundId: string;
  fundName: string;
  classes: ClassNameMap;
  viewSelected?: PresetExposureType;
  isFetching?: boolean;
}

//This is only here to deal with the render more hooks issue due to the buildDataFn having a context selector hook
const HedgePositionsTableWrapper: FunctionComponent<UltraTableWrapperProps> = ({
  originalData,
  fundId,
  fundName,
  classes,
  viewSelected,
  isFetching,
}) => {
  const [tableAggregate, setTableAggregate] = useState(true);
  // Defer the heavy rebuild so the switch stays responsive on large datasets
  const deferredAggregate = useDeferredValue(tableAggregate);
  const isAggregatePending = tableAggregate !== deferredAggregate;

  const data = useMemo(
    () => buildData(originalData, deferredAggregate),
    [originalData, deferredAggregate],
  );


  const columns = useMemo(
    () => buildColumns(deferredAggregate, ALL_EXPOSURE_COLUMNS, classes),
    [deferredAggregate, classes],
  );

  const tableData: TableData<EditablePosition> = useMemo(
    () => ({ columns, data }),
    [columns, data],
  );

  const totalAssetClasses = useMemo(() => {
    const assetClasses = new Set<string>();
    for (const item of data) {
      if (item?.asset_class) {
        assetClasses.add(item.asset_class);
      }
    }
    return assetClasses;
  }, [data]);

  const ultraTablePresets: UltraTablePresetConfig = useMemo(() => {
    const presets: UltraTablePreset[] = [];
    totalAssetClasses.forEach((asset) => {
      const preset = ALL_EXPOSURE_PRESETS.find((p) => p.name === asset);
      if (preset) {
        presets.push(preset);
      }
    });

    const contextPreset = ALL_EXPOSURE_PRESETS.find(
      (preset) => preset.id === viewSelected,
    );

    return {
      default: GeneralPreset,
      allPresets: presets,
      context: contextPreset,
      display: 'buttons',
    };
  }, [totalAssetClasses, viewSelected]);

  const positionDate = originalData.selected_position_date;

  return (
    <UltraTable<EditablePosition>
      title="Exposure Data"
      tableData={tableData}
      isFetching={isFetching}
      isAggregatePending={isAggregatePending}
      getRowCanExpand={() => true}
      pdfConfig={{
        pdfIdentifier: `${fundId}_exposure_main`,
        pdfTitle: `Exposure Data - ${fundName}`,
        pdfExportGroupName: 'exposure_page',
        pdfExportGroupOrder: 5,
        pdfFileName: `exposure-${fundId}-${positionDate || formatDateForCheckingState(new Date())
          }`,
      }}
      columnSelector
      actionBtns={[
        <FormControlLabel
          control={
            <Switch
              checked={tableAggregate}
              onChange={() => setTableAggregate((prev) => !prev)}
              className={classes.switch}
            />
          }
          label={'Aggregate Table'}
          className={classes.switchContainer}
        />,
      ]}
      positionDate={positionDate}
      nestedComponent={UnderlyingPositionTable}
      presets={ultraTablePresets}
      resetExpansion={deferredAggregate}
    />
  );
};

export interface EditablePosition extends Position, EditableFields { }
